import React, { FC, useState } from 'react';
import {
  Box,
  ClickAwayListener,
  Divider,
  Fade,
  Grow,
  MenuItem,
  MenuList,
  Paper,
  Popper,
  Stack,
  Typography,
} from '@mui/material';
import { Icon } from '@iconify/react';
import { usePermissions } from '../../hooks/usePermissions';
import TextField from '../../components/TextField/TextField';
import Button from '../../components/Button/Button';
import { LoadingDialogue } from '../../components/Spinners';
import MultipleVinsContainer from './MultipleVinsContainer';
// import SavedFiltersMenu from './SavedFiltersMenu';
import BulkUpdateModal from './BulkUpdate/BulkUpdateModal';
import { Vehicle } from '../../types';
import { useQuickFilter } from '../../hooks/useQuickFilter';
import { exportData } from '../../services/export';
import useSnackbarNotifications from '../../hooks/useSnackbarNotifications';

interface IActionList extends Vehicle {
  decision?: 'Rejected' | 'Approved';
}
type Props = {
  // onSearch: (searchText: string) => void;
  filteredVins: any;
  setInputText: any;
  inputText: any;
  setFilteredVins: any;
  onToggleToolPanelClick: (panelId: 'columns' | 'filters') => void;
  onBulkAction?: (event?: any) => void;
  handleChangeType: (event?: any) => void;
  handleAgentInventoryAssignment?: (event?: any) => void;
  handleImporterInventoryAssignment?: (event?: any) => void;
  handleManageStudioSelectDiscount?: (event?: any) => void;
  showBulkActionButton: boolean;
  bulkRef?: any;
  bulkToggleOpen?: any;
  paginationInfo?: any;
  uiHeadings?: any;
  onBulkUpdateCompleted?: () => void;
};

const GridTopActions: FC<Props> = ({
  // onSearch,
  filteredVins,
  setInputText,
  inputText,
  setFilteredVins,
  onToggleToolPanelClick,
  onBulkAction,
  handleChangeType,
  handleAgentInventoryAssignment,
  handleImporterInventoryAssignment,
  handleManageStudioSelectDiscount,
  showBulkActionButton,
  bulkRef,
  bulkToggleOpen,
  paginationInfo,
  uiHeadings,
  onBulkUpdateCompleted,
}) => {
  const { hasPermissions } = usePermissions();
  const [decisionPending, setDecisionPending] = useState(false);
  const [bulkUpdateOpen, setBulkUpdateOpen] = useState(false);
  // const requestSelected = paginated[selected];
  const { quickFilterParser, quickFilterMatcher, parseMultipleSearchText, multipleSearch } = useQuickFilter();
  const [open, setOpen] = React.useState(false);
  const anchorRef = React.useRef<HTMLButtonElement>(null);
  const [exportOpen, setExportOpen] = useState(false);
  const { showSuccessSnackbar, showErrorSnackbar } = useSnackbarNotifications();

  const inputHandler = (e: any) => {
    setInputText(e.target.value);
    // onSearch(e.target.value);
    const parsed = parseMultipleSearchText(e.target.value);
    if (multipleSearch(parsed)) {
      setInputText(parsed);
      setFilteredVins(parsed);
    }
    // eslint-disable-next-line react/no-array-index-key
    if (e.target.value === '') {
      setFilteredVins([]);
    }
  };

  const handleToggle = () => {
    setOpen((prevOpen) => !prevOpen);
  };

  const handleClose = (event: Event | React.SyntheticEvent) => {
    if (anchorRef.current && anchorRef.current.contains(event.target as HTMLElement)) {
      return;
    }

    setOpen(false);
  };

  function handleListKeyDown(event: React.KeyboardEvent) {
    if (event.key === 'Tab') {
      event.preventDefault();
      setOpen(false);
    } else if (event.key === 'Escape') {
      setOpen(false);
    }
  }

  // return focus to the button when we transitioned from !open -> open
  const prevOpen = React.useRef(open);
  React.useEffect(() => {
    if (prevOpen.current === true && open === false) {
      anchorRef.current!.focus();
    }

    prevOpen.current = open;
  }, [open]);

  const handleExport = async (type: string, event: Event | React.SyntheticEvent) => {
    handleClose(event);
    setExportOpen(true);
    try {
      const message = await exportData(type, 'Vehicle', paginationInfo, inputText, uiHeadings);
      if (message !== 'Success') {
        setExportOpen(false);
        throw new Error(message);
      } else {
        setExportOpen(false);
        showSuccessSnackbar({
          message: `The exported file is successfully sent to your email. Please check your Inbox.`,
        });
      }
    } catch (error) {
      console.log(error);
      showErrorSnackbar({ message: 'Error in sending the exported file.' });
    }
  };

  return (
    <Stack p={1}>
      <Stack direction="row" alignItems="center" gap={2} justifyContent="flex-end">
        <LoadingDialogue open={decisionPending} />
        <LoadingDialogue open={exportOpen} message="Please wait." />
        <Box width="100%">
          <TextField
            placeholder="Search..."
            sx={{ maxWidth: '100%' }}
            fullWidth
            value={inputText}
            endIcon={<Icon icon="ant-design:search-outlined" />}
            onChange={(e) => inputHandler(e)}
          />
        </Box>

        <Box sx={{ minWidth: '140px' }}>
          <Button
            size="small"
            startIcon={<Icon icon="fluent:column-triple-20-regular" />}
            onClick={() => onToggleToolPanelClick('columns')}
          >
            Manage Columns
          </Button>
        </Box>
        <Divider orientation="vertical" flexItem />
        <Box>
          <Button
            size="small"
            startIcon={<Icon icon="cil:list-filter" />}
            onClick={() => onToggleToolPanelClick('filters')}
          >
            Filters
          </Button>
        </Box>
        {hasPermissions(['CHANGE_ASSIGNMENT_ACTION']) && showBulkActionButton ? (
          <Box>
            <Button
              sx={{ border: `1px solid #456BD9`, borderRadius: 1, minWidth: '150px' }}
              size="small"
              ref={bulkRef}
              onClick={onBulkAction}
              endIcon={<Icon icon="akar-icons:chevron-down" />}
            >
              Bulk Action
            </Button>
            <Popper open={bulkToggleOpen} anchorEl={bulkRef.current} transition>
              {({ TransitionProps, placement }) => (
                <Grow
                  {...TransitionProps}
                  timeout={350}
                  style={{
                    transformOrigin: placement === 'bottom-start' ? 'left top' : 'left bottom',
                  }}
                >
                  <Paper>
                    <MenuList autoFocusItem={bulkToggleOpen} id="composition-menu" aria-labelledby="composition-button">
                      <MenuItem onClick={() => handleChangeType()}>
                        <Typography variant="body2" padding="15px">
                          Change Assignment Type
                        </Typography>
                      </MenuItem>
                      <Divider />
                      <MenuItem onClick={() => handleAgentInventoryAssignment?.()}>
                        <Typography variant="body2" padding="15px">
                          Agent Inventory Assignment
                        </Typography>
                      </MenuItem>
                      <MenuItem onClick={() => handleImporterInventoryAssignment?.()}>
                        <Typography variant="body2" padding="15px">
                          Importer Inventory Assignment
                        </Typography>
                      </MenuItem>
                      <MenuItem onClick={() => handleManageStudioSelectDiscount?.()}>
                        <Typography variant="body2" padding="15px">
                          Manage Studio Select Discount
                        </Typography>
                      </MenuItem>
                    </MenuList>
                  </Paper>
                </Grow>
              )}
            </Popper>
          </Box>
        ) : (
          ''
        )}
        {hasPermissions(['PERFORM_EXCEL_UPLOAD']) && showBulkActionButton ? (
          <Box>
            <Button
              sx={{ border: `1px solid #456BD9`, borderRadius: 1, minWidth: '140px' }}
              size="small"
              data-testid="bulk-update-button"
              onClick={() => setBulkUpdateOpen(true)}
              startIcon={<Icon icon="ph:upload-simple" />}
            >
              Bulk Update
            </Button>
          </Box>
        ) : (
          ''
        )}
        {showBulkActionButton && (
          <Box>
            <Button
              sx={{ border: `1px solid #456BD9`, borderRadius: 1, minWidth: '120px' }}
              size="small"
              ref={anchorRef}
              onClick={handleToggle}
              endIcon={<Icon icon="akar-icons:chevron-down" />}
            >
              Export
            </Button>
            <Popper open={open} anchorEl={anchorRef.current} role={undefined} placement="bottom-start" transition>
              {({ TransitionProps, placement }) => (
                <Grow
                  {...TransitionProps}
                  style={{
                    transformOrigin: placement === 'bottom-start' ? 'left top' : 'left bottom',
                  }}
                >
                  <Paper>
                    <ClickAwayListener onClickAway={handleClose}>
                      <MenuList
                        autoFocusItem={open}
                        id="composition-menu"
                        aria-labelledby="composition-button"
                        // overflow="visible"
                        // onKeyDown={handleListKeyDown}
                      >
                        <MenuItem onClick={(e) => handleExport('csv', e)}>CSV Export</MenuItem>
                        <MenuItem onClick={(e) => handleExport('excel', e)}>Excel Export</MenuItem>
                      </MenuList>
                    </ClickAwayListener>
                  </Paper>
                </Grow>
              )}
            </Popper>
          </Box>
        )}
      </Stack>
      {hasPermissions(['PERFORM_EXCEL_UPLOAD']) && (
        <BulkUpdateModal
          open={bulkUpdateOpen}
          onClose={() => setBulkUpdateOpen(false)}
          onCompleted={onBulkUpdateCompleted}
        />
      )}
      {filteredVins?.length > 1 && (
        <MultipleVinsContainer
          filteredVins={filteredVins}
          setFilteredVins={setFilteredVins}
          // onSearch={onSearch}
          setInputText={setInputText}
        />
      )}
    </Stack>
  );
};

export default GridTopActions;
